import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faPaperPlane,
  faPlus,
  faXmark,
  faCircleInfo
} from "@fortawesome/free-solid-svg-icons";
import useAuth from "../hooks/useAuth";
import useAxiosPrivate from "../hooks/useAxiosPrivate";

const Field = ({ id, label, required = false, children }) => (
  <div className="flex flex-col mt-2">
    <label htmlFor={id}>
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

const getEmailDomain = (email) => {
  const domain = String(email || "").split("@")[1];
  return domain?.trim().toLowerCase() || "";
};

const FluVoucherRequestForm = ({ orgInfo, policy, onClose, onSubmitted }) => {
  const { auth, createInfo, setShowLoader } = useAuth();

  const organisation =
    auth?.schools.filter(
      (school) => school?.urn === orgInfo?.registrationNumber,
    )[0] ?? null;

  const axios = useAxiosPrivate();
  const contactEmail = auth?.user?.contact_email || "";
  const contactEmailDomain = getEmailDomain(contactEmail);

  const [formData, setFormData] = useState({
    school_name: orgInfo?.name || "",
    registrationNumber: orgInfo?.registrationNumber || "",
    name: auth?.user?.contact_name || "",
    email: contactEmail,
    address: orgInfo?.address || "",
    domains: contactEmailDomain ? [contactEmailDomain] : [],
    numberOfVouchers: "",
  });

  const [domainInput, setDomainInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const closeTimeoutRef = useRef(null);

  useEffect(
    () => () => {
      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current);
      }
    },
    [],
  );

  const updateField = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const addDomain = () => {
    const domain = domainInput.trim().toLowerCase();
    if (!domain || formData.domains.includes(domain)) return;

    updateField("domains", [...formData.domains, domain]);
    setDomainInput("");
  };

  const removeDomain = (domainToRemove) => {
    updateField(
      "domains",
      formData.domains.filter((domain) => domain !== domainToRemove),
    );
  };

  const submitRequest = async (event) => {
    event.preventDefault();
    if (submitting) return;

    try {
      setSubmitting(true);
      setShowLoader(true);
      const response = await axios.post(
        "/service-requests/flu-voucher/create",
        {
          titleId: "fc9569f5-c591-49a7-b8c2-ccc85fa9ff41",

          policyId: policy?.policy_id,
          policy_reference: policy?.policy_reference,
          policy_department: policy?.department,

          orgId: organisation?.id,
          schoolName: formData.school_name,
          schoolAddress: formData.address,
          schoolPostcode: organisation?.postcode,

          contactId: auth?.user?.contact_id,
          contactName: formData.name,
          contactEmail: formData.email,

          additionalInfo: `Flu Voucher Request  - ${formData.numberOfVouchers} vouchers requested`,
          type: "flu-voucher",
          product: "Flu Voucher",
          name: "Flu Voucher",
          user_id: 100,
          urn: formData.registrationNumber,
          domains: formData.domains,
          numberOfVouchers: formData.numberOfVouchers,
        },
      );

      if (response.status !== 200) {
        createInfo("error", "Failed to request vouchers");
        return;
      }

      setSubmitted(true);
      onSubmitted?.();
      closeTimeoutRef.current = window.setTimeout(onClose, 2000);
    } catch (error) {
      createInfo("error", "Failed to submit request, please try again");
    } finally {
      setSubmitting(false);
      setShowLoader(false);
    }
  };

  const inputClassName = "border rounded-md py-2 px-4 w-full";

  if (submitted) {
    return (
      <div className="flex min-h-[300px] flex-col items-center justify-center text-center">
        <FontAwesomeIcon
          icon={faCircleCheck}
          className="mb-4 text-6xl text-green-500"
        />
        <div className="text-xl font-medium text-green-700">
          Flu voucher request submitted successfully
        </div>
        <p className="mt-2 text-gray-600">
          This window will close automatically.
        </p>
      </div>
    );
  }
  return (
    <form onSubmit={submitRequest}>
      <div className="text-[#02ADAD] text-lg font-medium">Client details</div>

      <div className="text-xs mt-2 mb-4">
        <span className="text-red-500">*</span> Denotes required fields.
      </div>
      <Field id="flu-school-name" label="School Name" required>
        <input
          id="flu-school-name"
          required
          className={inputClassName}
          value={formData.school_name}
          onChange={(event) => updateField("school_name", event.target.value)}
          placeholder="e.g. Abbott"
        />
      </Field>

      <div className="border-t mt-8 pt-6">
        <div className="text-[#02ADAD] text-lg font-medium">
          Primary contact details
        </div>
        <Field id="flu-name" label="Name" required>
          <input
            id="flu-name"
            required
            className={inputClassName}
            value={formData.name}
            onChange={(event) => updateField("name", event.target.value)}
            placeholder="e.g. John Doe"
          />
        </Field>
        <Field id="flu-email" label="Email" required>
          <input
            id="flu-email"
            type="email"
            required
            className={inputClassName}
            value={formData.email}
            onChange={(event) => updateField("email", event.target.value)}
            placeholder="e.g. johndoe@gmail.com"
          />
        </Field>
      </div>

      <div className="border-t mt-8 pt-6">
        <div className="text-[#02ADAD] text-lg font-medium">School Address</div>
        <Field id="flu-address" label="Address" required>
          <input
            id="flu-address"
            required
            className={inputClassName}
            value={formData.address}
            onChange={(event) => updateField("address", event.target.value)}
            placeholder="e.g. 101 Avenue A, London, SW1A 1AA"
          />
        </Field>
      </div>

      <div className="border-t mt-8 pt-6">
        <div className="text-[#02ADAD] text-lg font-medium">
          Number of Flu Vouchers
        </div>
        <Field
          id="flu-number"
          label="Please enter the number of flu vouchers you require"
          required
        >
          <input
            id="flu-number"
            required
            className={inputClassName}
            value={formData.numberOfVouchers}
            type="number"
            min={1}
            onChange={(event) =>
              updateField("numberOfVouchers", event.target.value)
            }
            placeholder="e.g. 100"
          />
        </Field>
      </div>

      <div className="border-t mt-8 pt-6">
        <label htmlFor="flu-domain" className="font-medium">
          Allowed email domains <span className="text-red-500">*</span>
        </label>
        <p className="mt-1 text-sm text-gray-600">
          This voucher will be restricted. Only people with an email domain in
          this list will be accepted when they sign up using the voucher.
        </p>
        <div className="mt-3 flex gap-2">
          <input
            id="flu-domain"
            type="text"
            className={inputClassName}
            value={domainInput}
            onChange={(event) =>
              setDomainInput(event.target.value.replace(/@/g, ""))
            }
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                addDomain();
              }
            }}
            placeholder="e.g. example.com"
          />
          <button
            type="button"
            onClick={addDomain}
            disabled={!domainInput.trim()}
            className="flex shrink-0 items-center gap-2 rounded-md bg-[#02ADAD] px-4 py-2 text-white hover:bg-[#029999] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faPlus} />
            Add
          </button>
        </div>
        {formData.domains.length > 0 && (
          <div
            className="mt-3 flex flex-wrap gap-2"
            aria-label="Allowed domains"
          >
            {formData.domains.map((domain) => (
              <span
                key={domain}
                className="inline-flex items-center gap-2 rounded-full bg-[#02ADAD]/10 px-3 py-1 text-sm text-[#027f7f]"
              >
                {domain}
                <button
                  type="button"
                  onClick={() => removeDomain(domain)}
                  className="rounded-full hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-[#02ADAD]"
                  aria-label={`Remove ${domain}`}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="border-t mt-8 pt-6">
        <div className="p-2 border-sky-100 bg-gradient-to-tr from-[#02ADAD] to-[#2ce8e8] rounded-lg text-white font-semibold flex justify-between items-center">
            <FontAwesomeIcon icon={faCircleInfo} className="text-3xl mr-2" />
            <p>Please be aware there could be a charge for your flu voucher allocation, please consult your {['Mutual', 'GBS'].includes(policy?.department) ? 'scheme' : 'policy'} wording or speak to your Account Manager if you are unsure.</p>
        </div>
      </div>
      <div className="hover:shadow-md w-11/12 mx-auto my-6">
        <button
          type="submit"
          disabled={submitting || formData.domains.length === 0}
          className="border border-[#02ADAD] flex items-center rounded h-[45px] w-full disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span className="text-[#02ADAD] h-full flex justify-center items-center px-10 font-medium w-full">
            {submitting ? "Submitting..." : "Submit Request"}
          </span>
          <span className="bg-[#02ADAD] text-white h-full flex w-[45px] justify-center items-center">
            <FontAwesomeIcon icon={faPaperPlane} />
          </span>
        </button>
      </div>
    </form>
  );
};

export default FluVoucherRequestForm;
